import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { MdSearch, MdNotifications, MdLightMode, MdDarkMode, MdReceipt, MdPersonOutline, MdClose, MdMenu, MdWarning, MdSchedule, MdPayment, MdPersonAdd, MdAlarm, MdCheckBox, MdCheckBoxOutlineBlank, MdDelete, MdAdd, MdPersonSearch, MdRequestQuote, MdPeople, MdToday } from 'react-icons/md';
import { clientesAPI, recibosAPI, pagosAPI, recordatoriosAPI, miDiaAPI } from '../../services/api';
import { useTheme } from '../../context/ThemeContext';

const ROUTE_MAP = {
  '/':                      { section: 'Análisis',  page: 'Dashboard' },
  '/mi-dia':                { section: 'Análisis',  page: 'Mi Día' },
  '/reportes':              { section: 'Análisis',  page: 'Reportes' },
  '/clientes':              { section: 'Clientes',  page: 'Clientes' },
  '/clientes/nuevo':        { section: 'Clientes',  page: 'Nuevo Cliente' },
  '/recibos':               { section: 'Clientes',  page: 'Recibos' },
  '/recibos/nuevo':         { section: 'Clientes',  page: 'Nuevo Recibo' },
  '/registrar-pago':        { section: 'Clientes',  page: 'Registrar Pago' },
  '/alertas':               { section: 'Gestión',   page: 'Cobranza' },
  '/difusion':              { section: 'Gestión',   page: 'Difusión' },
  '/plantillas':            { section: 'Gestión',   page: 'Plantillas WA' },
  '/leads':                 { section: 'Ventas',    page: 'Leads' },
  '/leads/nuevo':           { section: 'Ventas',    page: 'Nuevo Lead' },
  '/cotizaciones':          { section: 'Ventas',    page: 'Cotizaciones' },
  '/cotizaciones/nueva':    { section: 'Ventas',    page: 'Nueva Cotización' },
  '/pipeline':              { section: 'Ventas',    page: 'Pipeline' },
  '/inventario':            { section: 'Ventas',    page: 'Inventario' },
  '/inventario/nuevo':      { section: 'Ventas',    page: 'Nuevo Artículo' },
  '/pedidos':               { section: 'Ventas',    page: 'Pedidos' },
  '/pedidos/nuevo':         { section: 'Ventas',    page: 'Nuevo Pedido' },
  '/configuracion':         { section: 'Sistema',   page: 'Configuración' },
  '/usuarios':              { section: 'Sistema',   page: 'Usuarios' },
};

function getRouteInfo(pathname) {
  if (ROUTE_MAP[pathname]) return ROUTE_MAP[pathname];
  if (pathname.startsWith('/clientes/'))    return { section: 'Clientes', page: 'Detalle de Cliente' };
  if (pathname.startsWith('/recibos/'))     return { section: 'Clientes', page: 'Detalle de Recibo' };
  if (pathname.startsWith('/leads/'))       return { section: 'Ventas',   page: 'Detalle de Lead' };
  if (pathname.startsWith('/cotizaciones/'))return { section: 'Ventas',   page: 'Detalle de Cotización' };
  if (pathname.startsWith('/inventario/')) return { section: 'Ventas',   page: 'Detalle de Artículo' };
  if (pathname.startsWith('/pedidos/'))    return { section: 'Ventas',   page: 'Detalle de Pedido' };
  return { section: 'Análisis', page: 'Dashboard' };
}

const HeaderContainer = styled.header`
  background-color: ${p => p.theme.colors.background};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px ${p => p.theme.spacing.xl};
  gap: ${p => p.theme.spacing.lg};

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    padding: 12px ${p => p.theme.spacing.md};
    gap: ${p => p.theme.spacing.sm};
    border-bottom: 1px solid ${p => p.theme.colors.border};
  }
`;

/* Full-screen search overlay for mobile */
const MobileSearchOverlay = styled.div`
  display: none;

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    display: ${p => p.open ? 'flex' : 'none'};
    position: fixed;
    inset: 0;
    z-index: 300;
    flex-direction: column;
    background: ${p => p.theme.colors.backgroundLight};
  }
`;

const MobileSearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
  padding: ${p => p.theme.spacing.md};
  border-bottom: 1px solid ${p => p.theme.colors.border};
`;

const MobileSearchInput = styled.input`
  flex: 1;
  padding: 10px 14px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 99px;
  font-size: 1rem;
  font-family: ${p => p.theme.fonts.primary};
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};
  outline: none;

  &:focus { border-color: ${p => p.theme.colors.primary}; }
  &::placeholder { color: ${p => p.theme.colors.textLight}; }
`;

const MobileSearchResults = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const MobileSearchCloseBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  background: none;
  color: ${p => p.theme.colors.textSecondary};
  font-size: 1.5rem;
  cursor: pointer;
  flex-shrink: 0;
`;

const HamburgerBtn = styled.button`
  display: none;
  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    background: ${p => p.theme.colors.backgroundLight};
    border: 1px solid ${p => p.theme.colors.border};
    border-radius: 12px;
    color: ${p => p.theme.colors.textSecondary};
    font-size: 1.3rem;
    cursor: pointer;
    flex-shrink: 0;
    box-shadow: ${p => p.theme.shadows.sm};
    transition: ${p => p.theme.transitions.fast};
    &:hover { background: ${p => p.theme.colors.backgroundDark}; }
  }
`;

/* Search icon for mobile — triggers full-screen overlay */
const MobileSearchBtn = styled.button`
  display: none;
  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    background: ${p => p.theme.colors.backgroundLight};
    border: 1px solid ${p => p.theme.colors.border};
    border-radius: 12px;
    color: ${p => p.theme.colors.textSecondary};
    font-size: 1.3rem;
    cursor: pointer;
    flex-shrink: 0;
    box-shadow: ${p => p.theme.shadows.sm};
    transition: ${p => p.theme.transitions.fast};
    &:hover { background: ${p => p.theme.colors.backgroundDark}; }
  }
`;

const BreadcrumbBlock = styled.div`
  flex: 1;

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    text-align: center;
  }
`;

const Breadcrumb = styled.div`
  font-size: 0.7rem;
  font-weight: 500;
  color: ${p => p.theme.colors.textLight};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 2px;

  span { color: ${p => p.theme.colors.textSecondary}; }

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    display: none;
  }
`;

const PageTitle = styled.h1`
  font-size: 1.125rem;
  font-weight: 700;
  color: ${p => p.theme.colors.textPrimary};
  margin: 0;
  letter-spacing: -0.02em;

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    font-size: 1rem;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  width: 260px;

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    display: none;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 9px 14px 9px 2.25rem;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 99px;
  font-size: ${p => p.theme.fontSizes.sm};
  background-color: ${p => p.theme.colors.backgroundLight};
  color: ${p => p.theme.colors.textPrimary};
  font-family: ${p => p.theme.fonts.primary};
  transition: ${p => p.theme.transitions.fast};
  box-shadow: ${p => p.theme.shadows.sm};

  &:focus {
    border-color: ${p => p.theme.colors.primary};
    box-shadow: 0 0 0 3px ${p => p.theme.colors.primary}20;
    outline: none;
  }

  &::placeholder { color: ${p => p.theme.colors.textLight}; }
`;

const SearchIcon = styled(MdSearch)`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: ${p => p.theme.colors.textLight};
  font-size: 1.1rem;
  pointer-events: none;
`;

const ClearButton = styled.button`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: ${p => p.theme.colors.textLight};
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  border-radius: 4px;
  transition: ${p => p.theme.transitions.fast};

  &:hover { color: ${p => p.theme.colors.textPrimary}; }
  svg { font-size: 1rem; }
`;

const SuggestionsContainer = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  background-color: ${p => p.theme.colors.backgroundLight};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  box-shadow: ${p => p.theme.shadows.lg};
  max-height: 380px;
  overflow-y: auto;
  z-index: 1000;
`;

const SuggestionSection = styled.div`
  padding: ${p => p.theme.spacing.sm} 0;
  &:not(:last-child) { border-bottom: 1px solid ${p => p.theme.colors.border}; }
`;

const SectionTitle = styled.div`
  padding: ${p => p.theme.spacing.sm} ${p => p.theme.spacing.md};
  font-size: 0.65rem;
  font-weight: 700;
  color: ${p => p.theme.colors.textLight};
  text-transform: uppercase;
  letter-spacing: 0.07em;
`;

const SuggestionItem = styled.button`
  width: 100%;
  padding: ${p => p.theme.spacing.sm} ${p => p.theme.spacing.md};
  background: transparent;
  border: none;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.md};
  transition: ${p => p.theme.transitions.fast};
  color: ${p => p.theme.colors.textPrimary};

  &:hover { background-color: ${p => p.theme.colors.backgroundDark}; }

  svg {
    font-size: 1.25rem;
    color: ${p => p.theme.colors.primary};
    flex-shrink: 0;
  }
`;

const SuggestionContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const SuggestionTitle = styled.div`
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: 500;
  color: ${p => p.theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SuggestionSubtitle = styled.div`
  font-size: ${p => p.theme.fontSizes.xs};
  color: ${p => p.theme.colors.textLight};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const EmptyState = styled.div`
  padding: ${p => p.theme.spacing.lg};
  text-align: center;
  color: ${p => p.theme.colors.textLight};
  font-size: ${p => p.theme.fontSizes.sm};
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
`;

const IconButton = styled.button`
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${p => p.theme.colors.backgroundLight};
  border-radius: 12px;
  color: ${p => p.theme.colors.textSecondary};
  position: relative;
  transition: ${p => p.theme.transitions.fast};
  box-shadow: ${p => p.theme.shadows.sm};
  border: 1px solid ${p => p.theme.colors.border};

  svg { font-size: 1.2rem; }

  &:hover {
    background-color: ${p => p.theme.colors.backgroundDark};
    color: ${p => p.theme.colors.textPrimary};
  }
`;

const NotificationBadge = styled.span`
  position: absolute;
  top: 3px;
  right: 3px;
  min-width: 16px;
  height: 16px;
  padding: 0 3px;
  background-color: ${p => p.theme.colors.danger};
  color: white;
  font-size: 9px;
  font-weight: 700;
  border-radius: 99px;
  border: 2px solid ${p => p.theme.colors.backgroundLight};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NotifTabBar = styled.div`
  display: flex;
  gap: 2px;
  padding: 8px 10px;
  border-bottom: 1px solid ${p => p.theme.colors.border};
  flex-shrink: 0;
`;

const NotifTab = styled.button`
  flex: 1;
  padding: 5px 4px;
  font-size: 0.7rem;
  font-weight: ${p => p.active ? 700 : 500};
  font-family: ${p => p.theme.fonts.primary};
  background: ${p => p.active ? p.theme.colors.primary : 'transparent'};
  color: ${p => p.active ? 'white' : p.theme.colors.textSecondary};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: ${p => p.theme.transitions.fast};
  white-space: nowrap;
  &:hover:not([data-active="true"]) { background: ${p => p.theme.colors.backgroundDark}; color: ${p => p.theme.colors.textPrimary}; }
`;

const ActividadItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 16px;
  border-bottom: 1px solid ${p => p.theme.colors.borderLight};
  &:last-child { border-bottom: none; }
`;

const ActividadIcon = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 1rem;
  background: ${p =>
    p.tipo === 'pago' ? p.theme.colors.success + '20' :
    p.tipo === 'recibo' ? p.theme.colors.primary + '20' :
    p.theme.colors.info + '20'};
  color: ${p =>
    p.tipo === 'pago' ? p.theme.colors.success :
    p.tipo === 'recibo' ? p.theme.colors.primary :
    p.theme.colors.info};
`;

const ActividadInfo = styled.div`flex: 1; min-width: 0;`;

const ActividadTitle = styled.div`
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: 600;
  color: ${p => p.theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ActividadMeta = styled.div`
  font-size: ${p => p.theme.fontSizes.xs};
  color: ${p => p.theme.colors.textSecondary};
  margin-top: 2px;
`;

const ActividadFecha = styled.div`
  font-size: ${p => p.theme.fontSizes.xs};
  color: ${p => p.theme.colors.textLight};
  flex-shrink: 0;
  white-space: nowrap;
`;

const RecordatorioItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 16px;
  border-bottom: 1px solid ${p => p.theme.colors.borderLight};
  opacity: ${p => p.completado ? 0.5 : 1};
  &:last-child { border-bottom: none; }
`;

const RecordatorioCheck = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${p => p.completado ? p.theme.colors.success : p.theme.colors.textLight};
  font-size: 1.15rem;
  padding: 0;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-top: 1px;
  transition: ${p => p.theme.transitions.fast};
  &:hover { color: ${p => p.theme.colors.success}; }
`;

const RecordatorioInfo = styled.div`flex: 1; min-width: 0;`;

const RecordatorioTitulo = styled.div`
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: 600;
  color: ${p => p.theme.colors.textPrimary};
  text-decoration: ${p => p.completado ? 'line-through' : 'none'};
  word-break: break-word;
`;

const RecordatorioFecha = styled.div`
  font-size: ${p => p.theme.fontSizes.xs};
  color: ${p => p.vencido ? p.theme.colors.danger : p.theme.colors.textLight};
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const RecordatorioDelete = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${p => p.theme.colors.textLight};
  font-size: 1rem;
  padding: 0;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  transition: ${p => p.theme.transitions.fast};
  &:hover { color: ${p => p.theme.colors.danger}; }
`;

const AddRecordatorioForm = styled.div`
  padding: 12px 16px;
  border-top: 1px solid ${p => p.theme.colors.border};
  background: ${p => p.theme.colors.backgroundDark};
  flex-shrink: 0;
`;

const AddInput = styled.input`
  width: 100%;
  padding: 7px 10px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 8px;
  font-size: ${p => p.theme.fontSizes.sm};
  font-family: ${p => p.theme.fonts.primary};
  background: ${p => p.theme.colors.backgroundLight};
  color: ${p => p.theme.colors.textPrimary};
  margin-bottom: 6px;
  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
  &::placeholder { color: ${p => p.theme.colors.textLight}; }
`;

const AddRow = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
`;

const AddDateInput = styled.input`
  flex: 1;
  padding: 6px 8px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 8px;
  font-size: ${p => p.theme.fontSizes.xs};
  font-family: ${p => p.theme.fonts.primary};
  background: ${p => p.theme.colors.backgroundLight};
  color: ${p => p.theme.colors.textPrimary};
  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
`;

const AddBtn = styled.button`
  padding: 6px 12px;
  background: ${p => p.theme.colors.primary};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: ${p => p.theme.fontSizes.xs};
  font-weight: 600;
  font-family: ${p => p.theme.fonts.primary};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  transition: ${p => p.theme.transitions.fast};
  &:hover { opacity: 0.9; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const NotifWrapper = styled.div`
  position: relative;
`;

const NotifPanel = styled.div`
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  width: 360px;
  max-height: 480px;
  background: ${p => p.theme.colors.backgroundLight};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.lg};
  box-shadow: ${p => p.theme.shadows.lg};
  z-index: 1001;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: ${p => p.theme.breakpoints.mobile}) {
    width: calc(100vw - 32px);
    right: -8px;
  }
`;

const NotifPanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid ${p => p.theme.colors.border};
  flex-shrink: 0;
`;

const NotifPanelTitle = styled.span`
  font-size: ${p => p.theme.fontSizes.md};
  font-weight: 700;
  color: ${p => p.theme.colors.textPrimary};
  display: flex;
  align-items: center;
  gap: 8px;
  svg { color: ${p => p.theme.colors.primary}; }
`;


const NotifBody = styled.div`
  overflow-y: auto;
  flex: 1;
`;

const NotifSectionLabel = styled.div`
  padding: 10px 16px 4px;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: ${p => p.color || (p.overdue ? p.theme.colors.danger : p.theme.colors.warning)};
  display: flex;
  align-items: center;
  gap: 6px;
  svg { font-size: 0.85rem; }
`;

const NotifItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
  border-bottom: 1px solid ${p => p.theme.colors.borderLight};
  transition: ${p => p.theme.transitions.fast};
  &:hover { background: ${p => p.theme.colors.backgroundDark}; }
  &:last-child { border-bottom: none; }
`;

const NotifDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${p => p.color || (p.overdue ? p.theme.colors.danger : p.theme.colors.warning)};
  flex-shrink: 0;
`;

const NotifInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const NotifName = styled.div`
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: 600;
  color: ${p => p.theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const NotifMeta = styled.div`
  font-size: ${p => p.theme.fontSizes.xs};
  color: ${p => p.theme.colors.textSecondary};
  margin-top: 2px;
`;

const NotifAmount = styled.div`
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: 700;
  color: ${p => p.color || (p.overdue ? p.theme.colors.danger : p.theme.colors.warning)};
  flex-shrink: 0;
  white-space: nowrap;
`;

const NotifDivider = styled.div`
  height: 1px;
  background: ${p => p.theme.colors.border};
  margin: 4px 0;
`;

const NotifEmpty = styled.div`
  padding: 32px 16px;
  text-align: center;
  color: ${p => p.theme.colors.textLight};
  font-size: ${p => p.theme.fontSizes.sm};
`;

const NotifLoading = styled.div`
  padding: 32px 16px;
  text-align: center;
  color: ${p => p.theme.colors.textLight};
  font-size: ${p => p.theme.fontSizes.sm};
`;

const Header = ({ onMenuToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toggleTheme, isDark } = useTheme();
  const [alertasNoLeidas, setAlertasNoLeidas] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchResults, setSearchResults] = useState({ clientes: [], recibos: [] });
  const [isSearching, setIsSearching] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const mobileSearchInputRef = useRef(null);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const [notifTab, setNotifTab] = useState('cobros');
  const [miDiaData, setMiDiaData] = useState(null);
  const [notifLoading, setNotifLoading] = useState(false);
  const [actividad, setActividad] = useState([]);
  const [actividadLoading, setActividadLoading] = useState(false);
  const [recordatorios, setRecordatorios] = useState([]);
  const [recordatoriosLoading, setRecordatoriosLoading] = useState(false);
  const [newTitulo, setNewTitulo] = useState('');
  const [newFecha, setNewFecha] = useState('');
  const [savingRecordatorio, setSavingRecordatorio] = useState(false);
  const notifRef = useRef(null);

  const { section, page } = getRouteInfo(location.pathname);

  useEffect(() => {
    fetchMiDia();
    const interval = setInterval(fetchMiDia, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setShowNotifPanel(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (searchTerm.trim().length >= 2) {
      setIsSearching(true);
      debounceRef.current = setTimeout(() => performSearch(searchTerm), 300);
    } else {
      setSearchResults({ clientes: [], recibos: [] });
      setShowSuggestions(false);
    }

    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [searchTerm]);

  const fetchMiDia = async () => {
    setNotifLoading(true);
    try {
      const res = await miDiaAPI.get();
      const data = res.data?.data || null;
      setMiDiaData(data);
      setAlertasNoLeidas(data?.resumen?.total || 0);
    } catch {}
    finally { setNotifLoading(false); }
  };

  const toggleNotifPanel = () => {
    if (!showNotifPanel) {
      fetchMiDia();
      fetchActividad();
      fetchRecordatorios();
    }
    setShowNotifPanel(v => !v);
  };

  const fetchActividad = async () => {
    setActividadLoading(true);
    try {
      const hace14 = new Date(); hace14.setDate(hace14.getDate() - 14);
      const [pagosRes, recibosRes, clientesRes] = await Promise.all([
        pagosAPI.getAll(),
        recibosAPI.getAll(),
        clientesAPI.getAll(),
      ]);
      const pagos = (pagosRes.data.data || [])
        .filter(p => new Date(p.fecha_pago) >= hace14)
        .map(p => ({ tipo: 'pago', id: `p${p.id}`, titulo: p.cliente_nombre, meta: `$${parseFloat(p.monto).toLocaleString('es-MX', { minimumFractionDigits: 2 })} · ${p.metodo_pago || ''}`, fecha: p.fecha_pago || p.fecha_registro }));
      const recibos = (recibosRes.data.data || [])
        .filter(r => new Date(r.fecha_creacion || r.fecha_emision) >= hace14)
        .map(r => ({ tipo: 'recibo', id: `r${r.id}`, titulo: r.cliente_nombre, meta: `Recibo #${r.numero_recibo} · $${parseFloat(r.monto_total).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`, fecha: r.fecha_creacion || r.fecha_emision, reciboId: r.id }));
      const clientes = (clientesRes.data.data || [])
        .filter(c => new Date(c.fecha_registro) >= hace14)
        .map(c => ({ tipo: 'cliente', id: `c${c.id}`, titulo: c.nombre, meta: c.empresa || c.email || '', fecha: c.fecha_registro, clienteId: c.id }));
      const todo = [...pagos, ...recibos, ...clientes]
        .sort((a, b) => new Date(b.fecha) - new Date(a.fecha))
        .slice(0, 20);
      setActividad(todo);
    } catch {}
    finally { setActividadLoading(false); }
  };

  const fetchRecordatorios = async () => {
    setRecordatoriosLoading(true);
    try {
      const res = await recordatoriosAPI.getAll();
      setRecordatorios(res.data.data || []);
    } catch {}
    finally { setRecordatoriosLoading(false); }
  };

  const handleAddRecordatorio = async () => {
    if (!newTitulo.trim()) return;
    setSavingRecordatorio(true);
    try {
      const res = await recordatoriosAPI.create({ titulo: newTitulo.trim(), fecha_recordatorio: newFecha || null });
      setRecordatorios(prev => [res.data.data, ...prev]);
      setNewTitulo('');
      setNewFecha('');
    } catch {}
    finally { setSavingRecordatorio(false); }
  };

  const handleToggleRecordatorio = async (id) => {
    try {
      const res = await recordatoriosAPI.toggleCompletado(id);
      setRecordatorios(prev => prev.map(r => r.id === id ? res.data.data : r));
    } catch {}
  };

  const handleDeleteRecordatorio = async (id) => {
    try {
      await recordatoriosAPI.delete(id);
      setRecordatorios(prev => prev.filter(r => r.id !== id));
    } catch {}
  };

  const relativeDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr); d.setHours(0,0,0,0);
    const hoy = new Date(); hoy.setHours(0,0,0,0);
    const diff = Math.round((hoy - d) / 86400000);
    if (diff === 0) return 'Hoy';
    if (diff === 1) return 'Ayer';
    if (diff <= 6) return `Hace ${diff} días`;
    return d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
  };

  const isVencidoRecordatorio = (fecha) => {
    if (!fecha) return false;
    const d = new Date(fecha); d.setHours(0,0,0,0);
    const hoy = new Date(); hoy.setHours(0,0,0,0);
    return d < hoy;
  };

  const performSearch = async (term) => {
    try {
      const [clientesRes, recibosRes] = await Promise.all([
        clientesAPI.getAll(),
        recibosAPI.getAll(),
      ]);
      const clientes = clientesRes.data.data || [];
      const recibos = recibosRes.data.data || [];
      const q = term.toLowerCase();

      setSearchResults({
        clientes: clientes
          .filter(c => c.nombre?.toLowerCase().includes(q) || c.rfc?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q) || c.telefono?.includes(q))
          .slice(0, 5),
        recibos: recibos
          .filter(r => r.numero_recibo?.toString().includes(q) || r.cliente_nombre?.toLowerCase().includes(q) || r.concepto?.toLowerCase().includes(q))
          .slice(0, 5),
      });
      setShowSuggestions(true);
      setIsSearching(false);
    } catch (err) {
      console.error('Error al buscar:', err);
      setIsSearching(false);
      setSearchResults({ clientes: [], recibos: [] });
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setSearchResults({ clientes: [], recibos: [] });
    setShowSuggestions(false);
  };

  const handleNavigateToClient = (clienteId) => {
    navigate(`/clientes/${clienteId}`);
    setSearchTerm('');
    setShowSuggestions(false);
  };

  const handleNavigateToReceipt = (reciboId) => {
    navigate(`/recibos/${reciboId}`);
    setSearchTerm('');
    setShowSuggestions(false);
  };

  const formatCurrency = (amount) =>
    `$${parseFloat(amount || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('es-MX', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const hasResults = searchResults.clientes.length > 0 || searchResults.recibos.length > 0;

  const openMobileSearch = () => {
    setMobileSearchOpen(true);
    setTimeout(() => mobileSearchInputRef.current?.focus(), 80);
  };

  const closeMobileSearch = () => {
    setMobileSearchOpen(false);
    handleClearSearch();
  };

  const renderSuggestions = () => (
    isSearching ? (
      <EmptyState>Buscando...</EmptyState>
    ) : hasResults ? (
      <>
        {searchResults.clientes.length > 0 && (
          <SuggestionSection>
            <SectionTitle>Clientes</SectionTitle>
            {searchResults.clientes.map(cliente => (
              <SuggestionItem key={cliente.id} onClick={() => { handleNavigateToClient(cliente.id); closeMobileSearch(); }}>
                <MdPersonOutline />
                <SuggestionContent>
                  <SuggestionTitle>{cliente.nombre}</SuggestionTitle>
                  <SuggestionSubtitle>
                    {cliente.rfc && `RFC: ${cliente.rfc}`}
                    {cliente.rfc && cliente.telefono && ' • '}
                    {cliente.telefono && `Tel: ${cliente.telefono}`}
                  </SuggestionSubtitle>
                </SuggestionContent>
              </SuggestionItem>
            ))}
          </SuggestionSection>
        )}
        {searchResults.recibos.length > 0 && (
          <SuggestionSection>
            <SectionTitle>Recibos</SectionTitle>
            {searchResults.recibos.map(recibo => (
              <SuggestionItem key={recibo.id} onClick={() => { handleNavigateToReceipt(recibo.id); closeMobileSearch(); }}>
                <MdReceipt />
                <SuggestionContent>
                  <SuggestionTitle>Recibo #{recibo.numero_recibo} — {recibo.cliente_nombre}</SuggestionTitle>
                  <SuggestionSubtitle>
                    {recibo.concepto && `${recibo.concepto} • `}
                    {formatCurrency(recibo.monto_total)}
                    {recibo.fecha_emision && ` • ${formatDate(recibo.fecha_emision)}`}
                  </SuggestionSubtitle>
                </SuggestionContent>
              </SuggestionItem>
            ))}
          </SuggestionSection>
        )}
      </>
    ) : searchTerm.trim().length >= 2 ? (
      <EmptyState>Sin resultados para "{searchTerm}"</EmptyState>
    ) : null
  );

  return (
    <>
    {/* Mobile full-screen search overlay */}
    <MobileSearchOverlay open={mobileSearchOpen}>
      <MobileSearchBar>
        <MobileSearchInput
          ref={mobileSearchInputRef}
          type="text"
          placeholder="Buscar clientes, recibos..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
        <MobileSearchCloseBtn onClick={closeMobileSearch}><MdClose /></MobileSearchCloseBtn>
      </MobileSearchBar>
      <MobileSearchResults>
        {renderSuggestions()}
      </MobileSearchResults>
    </MobileSearchOverlay>

    <HeaderContainer>
      <HamburgerBtn onClick={onMenuToggle} aria-label="Abrir menú">
        <MdMenu />
      </HamburgerBtn>
      <img src={`${process.env.PUBLIC_URL}/logo/logo.png`} alt="Aether" style={{ height: '56px', width: '56px', objectFit: 'contain', flexShrink: 0 }} />
      <BreadcrumbBlock>
        <Breadcrumb>{section}</Breadcrumb>
        <PageTitle>{page}</PageTitle>
      </BreadcrumbBlock>

      <SearchContainer ref={searchRef}>
        <SearchIcon />
        <SearchInput
          type="text"
          placeholder="Buscar..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          onFocus={() => searchTerm.trim().length >= 2 && setShowSuggestions(true)}
        />
        {searchTerm && (
          <ClearButton onClick={handleClearSearch}><MdClose /></ClearButton>
        )}

        {showSuggestions && (
          <SuggestionsContainer>
            {isSearching ? (
              <EmptyState>Buscando...</EmptyState>
            ) : hasResults ? (
              <>
                {searchResults.clientes.length > 0 && (
                  <SuggestionSection>
                    <SectionTitle>Clientes</SectionTitle>
                    {searchResults.clientes.map(cliente => (
                      <SuggestionItem key={cliente.id} onClick={() => handleNavigateToClient(cliente.id)}>
                        <MdPersonOutline />
                        <SuggestionContent>
                          <SuggestionTitle>{cliente.nombre}</SuggestionTitle>
                          <SuggestionSubtitle>
                            {cliente.rfc && `RFC: ${cliente.rfc}`}
                            {cliente.rfc && cliente.telefono && ' • '}
                            {cliente.telefono && `Tel: ${cliente.telefono}`}
                          </SuggestionSubtitle>
                        </SuggestionContent>
                      </SuggestionItem>
                    ))}
                  </SuggestionSection>
                )}
                {searchResults.recibos.length > 0 && (
                  <SuggestionSection>
                    <SectionTitle>Recibos</SectionTitle>
                    {searchResults.recibos.map(recibo => (
                      <SuggestionItem key={recibo.id} onClick={() => handleNavigateToReceipt(recibo.id)}>
                        <MdReceipt />
                        <SuggestionContent>
                          <SuggestionTitle>Recibo #{recibo.numero_recibo} — {recibo.cliente_nombre}</SuggestionTitle>
                          <SuggestionSubtitle>
                            {recibo.concepto && `${recibo.concepto} • `}
                            {formatCurrency(recibo.monto_total)}
                            {recibo.fecha_emision && ` • ${formatDate(recibo.fecha_emision)}`}
                          </SuggestionSubtitle>
                        </SuggestionContent>
                      </SuggestionItem>
                    ))}
                  </SuggestionSection>
                )}
              </>
            ) : (
              <EmptyState>Sin resultados para "{searchTerm}"</EmptyState>
            )}
          </SuggestionsContainer>
        )}
      </SearchContainer>

      <MobileSearchBtn onClick={openMobileSearch} aria-label="Buscar">
        <MdSearch />
      </MobileSearchBtn>

      <Actions>
        <NotifWrapper ref={notifRef}>
          <IconButton onClick={toggleNotifPanel} title="Notificaciones">
            <MdNotifications />
            {alertasNoLeidas > 0 && (
              <NotificationBadge>{alertasNoLeidas > 99 ? '99+' : alertasNoLeidas}</NotificationBadge>
            )}
          </IconButton>

          {showNotifPanel && (
            <NotifPanel>
              <NotifPanelHeader>
                <NotifPanelTitle>
                  <MdNotifications /> Notificaciones
                </NotifPanelTitle>
              </NotifPanelHeader>

              <NotifTabBar>
                <NotifTab active={notifTab === 'cobros'} data-active={notifTab === 'cobros'} onClick={() => setNotifTab('cobros')}>
                  Cobros {alertasNoLeidas > 0 ? `(${alertasNoLeidas})` : ''}
                </NotifTab>
                <NotifTab active={notifTab === 'actividad'} data-active={notifTab === 'actividad'} onClick={() => setNotifTab('actividad')}>
                  Actividad
                </NotifTab>
                <NotifTab active={notifTab === 'recordatorios'} data-active={notifTab === 'recordatorios'} onClick={() => setNotifTab('recordatorios')}>
                  Recordatorios {recordatorios.filter(r => !r.completado).length > 0 ? `(${recordatorios.filter(r => !r.completado).length})` : ''}
                </NotifTab>
              </NotifTabBar>

              {/* ── Tab: Cobros (Mi Día) ── */}
              {notifTab === 'cobros' && (
                <NotifBody>
                  {notifLoading ? (
                    <NotifLoading>Cargando...</NotifLoading>
                  ) : !miDiaData || miDiaData.resumen?.total === 0 ? (
                    <NotifEmpty>Todo al día ✓</NotifEmpty>
                  ) : (
                    <>
                      {/* Leads estancados */}
                      {miDiaData.leadsEstancados?.length > 0 && (
                        <>
                          <NotifSectionLabel color="#f59e0b">
                            <MdPersonSearch /> Leads estancados · {miDiaData.leadsEstancados.length}
                          </NotifSectionLabel>
                          {miDiaData.leadsEstancados.slice(0, 4).map(l => (
                            <NotifItem key={`l-${l.id}`} onClick={() => { navigate(`/leads/${l.id}`); setShowNotifPanel(false); }}>
                              <NotifDot color="#f59e0b" />
                              <NotifInfo>
                                <NotifName>{l.nombre}{l.empresa ? ` — ${l.empresa}` : ''}</NotifName>
                                <NotifMeta>{l.dias_sin_actividad}d sin actividad · {l.estado}</NotifMeta>
                              </NotifInfo>
                            </NotifItem>
                          ))}
                          <NotifDivider />
                        </>
                      )}

                      {/* Recibos por vencer */}
                      {miDiaData.recibosPróximos?.length > 0 && (
                        <>
                          <NotifSectionLabel color="#ef4444">
                            <MdReceipt /> Recibos por vencer · {miDiaData.recibosPróximos.length}
                          </NotifSectionLabel>
                          {miDiaData.recibosPróximos.slice(0, 4).map(r => (
                            <NotifItem key={`r-${r.id}`} onClick={() => { navigate(`/recibos/${r.id}`); setShowNotifPanel(false); }}>
                              <NotifDot color={r.dias_para_vencer === 0 ? '#ef4444' : '#f59e0b'} />
                              <NotifInfo>
                                <NotifName>{r.cliente_nombre}</NotifName>
                                <NotifMeta>{r.dias_para_vencer === 0 ? 'Vence hoy' : `${r.dias_para_vencer}d para vencer`} · #{r.numero_recibo}</NotifMeta>
                              </NotifInfo>
                              <NotifAmount color="#ef4444">${parseFloat(r.saldo_pendiente || 0).toLocaleString('es-MX', { minimumFractionDigits: 0 })}</NotifAmount>
                            </NotifItem>
                          ))}
                          <NotifDivider />
                        </>
                      )}

                      {/* Cotizaciones sin respuesta */}
                      {miDiaData.cotizacionesSinRespuesta?.length > 0 && (
                        <>
                          <NotifSectionLabel color="#8b5cf6">
                            <MdRequestQuote /> Cotizaciones sin respuesta · {miDiaData.cotizacionesSinRespuesta.length}
                          </NotifSectionLabel>
                          {miDiaData.cotizacionesSinRespuesta.slice(0, 3).map(c => (
                            <NotifItem key={`c-${c.id}`} onClick={() => { navigate(`/cotizaciones/${c.id}`); setShowNotifPanel(false); }}>
                              <NotifDot color="#8b5cf6" />
                              <NotifInfo>
                                <NotifName>{c.cliente_nombre}</NotifName>
                                <NotifMeta>{c.dias_sin_respuesta}d sin respuesta · ${parseFloat(c.monto_total || 0).toLocaleString('es-MX', { minimumFractionDigits: 0 })}</NotifMeta>
                              </NotifInfo>
                            </NotifItem>
                          ))}
                          <NotifDivider />
                        </>
                      )}

                      {/* Clientes sin cobro */}
                      {miDiaData.clientesSinContacto?.length > 0 && (
                        <>
                          <NotifSectionLabel color="#0891b2">
                            <MdPeople /> Clientes sin cobro · {miDiaData.clientesSinContacto.length}
                          </NotifSectionLabel>
                          {miDiaData.clientesSinContacto.slice(0, 3).map(c => (
                            <NotifItem key={`cl-${c.id}`} onClick={() => { navigate(`/clientes/${c.id}`); setShowNotifPanel(false); }}>
                              <NotifDot color="#0891b2" />
                              <NotifInfo>
                                <NotifName>{c.nombre}</NotifName>
                                <NotifMeta>{c.ultimo_pago ? `${c.dias_sin_pago}d sin pago` : 'Sin pagos'} · Saldo ${parseFloat(c.saldo_pendiente || 0).toLocaleString('es-MX', { minimumFractionDigits: 0 })}</NotifMeta>
                              </NotifInfo>
                            </NotifItem>
                          ))}
                        </>
                      )}

                      <NotifItem onClick={() => { navigate('/mi-dia'); setShowNotifPanel(false); }} style={{ justifyContent: 'center', color: 'inherit', opacity: 0.6, fontSize: '0.75rem' }}>
                        <MdToday style={{ marginRight: 6 }} /> Ver todo en Mi Día
                      </NotifItem>
                    </>
                  )}
                </NotifBody>
              )}

              {/* ── Tab: Actividad ── */}
              {notifTab === 'actividad' && (
                <NotifBody>
                  {actividadLoading ? (
                    <NotifLoading>Cargando...</NotifLoading>
                  ) : actividad.length === 0 ? (
                    <NotifEmpty>Sin actividad reciente</NotifEmpty>
                  ) : actividad.map(item => (
                    <ActividadItem key={item.id}>
                      <ActividadIcon tipo={item.tipo}>
                        {item.tipo === 'pago' && <MdPayment />}
                        {item.tipo === 'recibo' && <MdReceipt />}
                        {item.tipo === 'cliente' && <MdPersonAdd />}
                      </ActividadIcon>
                      <ActividadInfo>
                        <ActividadTitle>
                          {item.tipo === 'pago' ? 'Pago recibido' : item.tipo === 'recibo' ? 'Recibo creado' : 'Cliente nuevo'} — {item.titulo}
                        </ActividadTitle>
                        <ActividadMeta>{item.meta}</ActividadMeta>
                      </ActividadInfo>
                      <ActividadFecha>{relativeDate(item.fecha)}</ActividadFecha>
                    </ActividadItem>
                  ))}
                </NotifBody>
              )}

              {/* ── Tab: Recordatorios ── */}
              {notifTab === 'recordatorios' && (
                <>
                  <NotifBody>
                    {recordatoriosLoading ? (
                      <NotifLoading>Cargando...</NotifLoading>
                    ) : recordatorios.length === 0 ? (
                      <NotifEmpty>Sin recordatorios. Agrega uno abajo.</NotifEmpty>
                    ) : recordatorios.map(r => (
                      <RecordatorioItem key={r.id} completado={r.completado}>
                        <RecordatorioCheck completado={r.completado} onClick={() => handleToggleRecordatorio(r.id)}>
                          {r.completado ? <MdCheckBox /> : <MdCheckBoxOutlineBlank />}
                        </RecordatorioCheck>
                        <RecordatorioInfo>
                          <RecordatorioTitulo completado={r.completado}>{r.titulo}</RecordatorioTitulo>
                          {r.fecha_recordatorio && (
                            <RecordatorioFecha vencido={!r.completado && isVencidoRecordatorio(r.fecha_recordatorio)}>
                              <MdAlarm style={{ fontSize: '0.8rem' }} />
                              {new Date(r.fecha_recordatorio).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </RecordatorioFecha>
                          )}
                        </RecordatorioInfo>
                        <RecordatorioDelete onClick={() => handleDeleteRecordatorio(r.id)}>
                          <MdDelete />
                        </RecordatorioDelete>
                      </RecordatorioItem>
                    ))}
                  </NotifBody>

                  <AddRecordatorioForm>
                    <AddInput
                      type="text"
                      placeholder="Nuevo recordatorio..."
                      value={newTitulo}
                      onChange={e => setNewTitulo(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleAddRecordatorio()}
                    />
                    <AddRow>
                      <AddDateInput
                        type="date"
                        value={newFecha}
                        onChange={e => setNewFecha(e.target.value)}
                      />
                      <AddBtn onClick={handleAddRecordatorio} disabled={savingRecordatorio || !newTitulo.trim()}>
                        <MdAdd /> Agregar
                      </AddBtn>
                    </AddRow>
                  </AddRecordatorioForm>
                </>
              )}
            </NotifPanel>
          )}
        </NotifWrapper>
        <IconButton onClick={toggleTheme} title={isDark ? 'Modo claro' : 'Modo oscuro'}>
          {isDark ? <MdLightMode /> : <MdDarkMode />}
        </IconButton>
      </Actions>
    </HeaderContainer>
    </>
  );
};

export default Header;
